/**
 * Custom Search Modal
 * Features:
 * - Shows bestseller products initially (loaded from Liquid)
 * - Real-time search with 300ms debounce
 * - Max 4 search results
 * - "View all results" link when more than 4 results exist
 * - Only searches products (no collections/pages)
 */

class CustomSearchModal {
  constructor() {
    this.modal = document.getElementById('customSearchModal');
    this.overlay = document.getElementById('customSearchOverlay');
    this.closeBtn = document.getElementById('customSearchClose');
    this.searchInput = document.getElementById('customSearchInput');

    this.bestsellerSection = document.getElementById('bestsellerSection');
    this.searchResultsSection = document.getElementById('searchResultsSection');
    this.searchResultsGrid = document.getElementById('searchResultsGrid');
    this.searchResultsTitle = document.getElementById('searchResultsTitle');
    this.viewAllResults = document.getElementById('viewAllResults');
    this.viewAllResultsLink = document.getElementById('viewAllResultsLink');
    this.noResultsMessage = document.getElementById('noResultsMessage');
    this.loadingState = document.getElementById('loadingState');

    this.debounceTimer = null;
    this.debounceDelay = 300; // 300ms delay
    this.maxResults = 4;
    this.currentQuery = '';

    this.init();
  }

  init() {
    // Close button click
    this.closeBtn?.addEventListener('click', () => this.close());

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Search input with debounce
    this.searchInput?.addEventListener('input', (e) => {
      this.handleSearchInput(e.target.value);
    });

    // Prevent modal close when clicking inside modal
    this.modal?.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  open() {
    this.modal?.classList.add('active');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus on search input
    setTimeout(() => {
      this.searchInput?.focus();
    }, 100);
  }

  close() {
    this.modal?.classList.remove('active');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';

    // Reset search
    this.resetSearch();
  }

  isOpen() {
    return this.modal?.classList.contains('active');
  }

  resetSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.currentQuery = '';
    this.showBestseller();
    this.hideSearchResults();
  }

  showBestseller() {
    this.bestsellerSection?.classList.remove('custom-search-section--hidden');
  }

  hideBestseller() {
    this.bestsellerSection?.classList.add('custom-search-section--hidden');
  }

  showSearchResults() {
    this.searchResultsSection?.classList.remove('custom-search-section--hidden');
  }

  hideSearchResults() {
    this.searchResultsSection?.classList.add('custom-search-section--hidden');
  }

  showLoading() {
    this.loadingState?.classList.remove('custom-search-loading--hidden');
    this.hideNoResults();
    this.hideViewAll();
  }

  hideLoading() {
    this.loadingState?.classList.add('custom-search-loading--hidden');
  }

  showNoResults() {
    this.noResultsMessage?.classList.remove('custom-search-no-results--hidden');
  }

  hideNoResults() {
    this.noResultsMessage?.classList.add('custom-search-no-results--hidden');
  }

  showViewAll(query) {
    if (this.viewAllResults && this.viewAllResultsLink) {
      this.viewAllResults.classList.remove('custom-search-view-all--hidden');
      this.viewAllResultsLink.href = `/search?q=${encodeURIComponent(query)}`;
    }
  }

  hideViewAll() {
    this.viewAllResults?.classList.add('custom-search-view-all--hidden');
  }

  handleSearchInput(value) {
    // Clear previous timer
    clearTimeout(this.debounceTimer);

    const query = value.trim();

    // If empty, show bestseller
    if (query === '') {
      this.resetSearch();
      return;
    }

    // Set debounce timer
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceDelay);
  }

  async performSearch(query) {
    this.currentQuery = query;

    // Hide bestseller, show search results section
    this.hideBestseller();
    this.showSearchResults();
    this.showLoading();
    this.searchResultsGrid.innerHTML = '';

    try {
      // Use Shopify Predictive Search API
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      this.hideLoading();

      // Get products from response
      const products = data.resources?.results?.products || [];

      if (products.length === 0) {
        this.showNoResults();
        this.searchResultsGrid.innerHTML = '';
        return;
      }

      // Show max 4 results
      const displayProducts = products.slice(0, this.maxResults);
      const hasMore = products.length > this.maxResults;

      // Render products
      this.renderProducts(displayProducts);

      // Show "View all results" if more than 4
      if (hasMore) {
        this.showViewAll(query);
      } else {
        this.hideViewAll();
      }

    } catch (error) {
      console.error('Search error:', error);
      this.hideLoading();
      this.showNoResults();
    }
  }

  renderProducts(products) {
    this.hideNoResults();

    const productsHTML = products.map(product => {
      return this.createProductCard(product);
    }).join('');

    this.searchResultsGrid.innerHTML = productsHTML;
  }

  createProductCard(product) {
    const price = this.formatPrice(product.price);
    // Handle both string URL and object format from Shopify API
    const imageUrl = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.url || product.image || '';
    const productType = product.product_type || 'Product';

    return `
      <a href="${product.url}" class="custom-search-product-card">
        <div class="custom-search-product-image-wrapper">
          ${imageUrl ?
            `<img
              src="${imageUrl}"
              alt="${this.escapeHtml(product.title)}"
              class="custom-search-product-image"
              loading="lazy"
              width="238"
              height="320"
            >` :
            `<div class="custom-search-product-image custom-search-product-image--placeholder"></div>`
          }
        </div>
        <div class="custom-search-product-info">
          <p class="custom-search-product-category">${this.escapeHtml(productType)}</p>
          <h3 class="custom-search-product-name">${this.escapeHtml(product.title)}</h3>
          <p class="custom-search-product-price">${price}</p>
        </div>
      </a>
    `;
  }

  formatPrice(price) {
    // Shopify Predictive Search API returns price as is (not in cents)
    // Just format it properly
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;

    // Format with Indian numbering system
    return `Rs. ${Math.round(priceNum).toLocaleString('en-IN')}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize the search modal
let customSearchModalInstance;

document.addEventListener('DOMContentLoaded', () => {
  customSearchModalInstance = new CustomSearchModal();
});

// Global function to open the modal (can be called from anywhere)
function openCustomSearchModal() {
  if (customSearchModalInstance) {
    customSearchModalInstance.open();
  }
}

// Global function to close the modal (optional)
function closeCustomSearchModal() {
  if (customSearchModalInstance) {
    customSearchModalInstance.close();
  }
}
